import React, { useCallback, useEffect, useRef, useState } from "react";

const TOKEN_KEY = "ct_login_token";
const USER_KEY = "ct_login_user";
const API_BASE = window.location.hostname === "localhost" ? "http://localhost:5000" : "";

function readJson(key, fallback) {
  try { return JSON.parse(localStorage.getItem(key) || JSON.stringify(fallback)); }
  catch { return fallback; }
}

function getLoginUser() {
  return readJson(USER_KEY, null);
}

function getOptionType(order) {
  const raw = String(order.optionType || order.drvOptionType || order.optType || "").toUpperCase();
  if (raw === "CE" || raw.includes("CALL")) return "CE";
  if (raw === "PE" || raw.includes("PUT")) return "PE";

  const text = `${order.tradingSymbol || ""} ${order.symbol || ""} ${order.displayName || ""}`.toUpperCase();
  if (text.includes(" CALL") || text.includes(" CE") || text.endsWith("CE")) return "CE";
  if (text.includes(" PUT") || text.includes(" PE") || text.endsWith("PE")) return "PE";
  return "";
}

function normalizeOrder(order) {
  const txn = String(order.transactionType || order.txnType || "").toUpperCase();
  const status = String(order.orderStatus || order.status || "").toUpperCase();

  return {
    txn: txn.startsWith("B") ? "BUY" : txn.startsWith("S") ? "SELL" : txn,
    status,
    optionType: getOptionType(order),
    symbol: order.tradingSymbol || order.symbol || order.displayName || "-",
    qty: Number(order.quantity || order.filledQty || order.remainingQuantity || 0),
    time: order.createTime || order.updateTime || order.exchangeTime || order.orderDateTime || "",
  };
}

function latestParentBuyOptionSide(orders) {
  const valid = orders
    .filter(o => o.txn === "BUY")
    .filter(o => !o.status.includes("REJECT") && !o.status.includes("CANCEL"))
    .sort((a, b) => new Date(b.time || 0) - new Date(a.time || 0));

  const latest = valid[0];
  if (!latest) return { side: "", orderType: "", latestBuy: null, liveCount: 0 };

  return {
    side: latest.optionType === "CE" ? "BUY" : latest.optionType === "PE" ? "SELL" : "",
    orderType: latest.optionType,
    latestBuy: latest,
    liveCount: valid.length,
  };
}

function getMarketRule(orderType, metrics = {}) {
  const ceVolume = Number(metrics.ceVolume || 0);
  const peVolume = Number(metrics.peVolume || 0);
  const callSellers = Number(metrics.callSellers || 0);
  const putSellers = Number(metrics.putSellers || 0);
  const pcr = Number(metrics.pcr || (callSellers > 0 ? putSellers / callSellers : 0));

  if (orderType === "CE") {
    return {
      aligned: ceVolume > peVolume && putSellers > callSellers && pcr > 1,
      expected: "CE BUY needs CE Volume > PE Volume, Put Sellers > Call Sellers, PCR > 1",
    };
  }

  if (orderType === "PE") {
    return {
      aligned: peVolume > ceVolume && callSellers > putSellers && pcr < 1,
      expected: "PE BUY needs PE Volume > CE Volume, Call Sellers > Put Sellers, PCR < 1",
    };
  }

  return { aligned: false, expected: "No CE/PE order type found" };
}

async function loadCopyData() {
  const token = localStorage.getItem(TOKEN_KEY);
  const loginUser = getLoginUser();
  let groups = [];
  let accounts = [];
  let source = "localStorage";

  if (token) {
    try {
      const [groupsRes, accountsRes] = await Promise.all([
        fetch(`${API_BASE}/api/login/data/groups`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE}/api/login/data/accounts`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      const g = await groupsRes.json().catch(() => ({}));
      const a = await accountsRes.json().catch(() => ({}));

      if (groupsRes.ok && g.success) { groups = g.groups || []; source = "backend"; }
      if (accountsRes.ok && a.success) { accounts = a.accounts || []; source = "backend"; }
    } catch {}
  }

  if (!groups.length) groups = [
    ...readJson(loginUser?.id ? `ct_groups_${loginUser.id}` : "ct_groups_guest", []),
    ...readJson("ct_groups", []),
  ];

  if (!accounts.length) accounts = [
    ...readJson(loginUser?.id ? `ct_accounts_${loginUser.id}` : "ct_accounts_guest", []),
    ...readJson("ct_accounts", []),
  ];

  return { groups, accounts, source };
}

function getParentAccounts(groups, accounts) {
  const parentIds = [...new Set((groups || []).filter(g => g?.parent && g.active !== false).map(g => String(g.parent)))];

  return parentIds.map(parentId => {
    const acc = (accounts || []).find(a =>
      String(a.id) === parentId ||
      String(a.clientId || "") === parentId ||
      String(a.userId || "") === parentId
    );

    return acc ? { ...acc, parentId } : { id: parentId, parentId, missingAccount: true };
  });
}

export default function ParentOrderAlignmentPanel({ marketSide, marketMetrics, title = "PARENT ORDER ALIGNMENT CHECK" }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(null);
  const timerRef = useRef(null);
  const marketMetricsRef = useRef(marketMetrics);
  marketMetricsRef.current = marketMetrics;

  const check = useCallback(async () => {
    if (!marketSide) {
      setState({ status: "neutral", color: "#f59e0b", headline: "MARKET SIGNAL IS NEUTRAL", message: "No parent order alignment needed right now." });
      return;
    }

    try {
      const { groups, accounts, source } = await loadCopyData();
      const parents = getParentAccounts(groups, accounts);
      const usable = parents.filter(p => !p.missingAccount && p.accessToken && (!p.broker || String(p.broker).toLowerCase() === "dhan"));

      if (!usable.length) {
        setState({ status: "missing", color: "#f59e0b", headline: "GROUP PARENT ACCOUNT TOKEN NOT FOUND", message: "Selected group parent account token was not found.", source });
        return;
      }

      const ordersByParent = await Promise.all(usable.map(async p => {
        const clientId = p.clientId || p.id || p.userId || p.parentId;
        const res = await fetch(`${API_BASE}/api/orders/dhan`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ clientId, accessToken: p.accessToken }),
        });
        const raw = await res.json().catch(() => ({}));
        return raw.orders || [];
      }));

      const orderBias = latestParentBuyOptionSide(ordersByParent.flat().map(normalizeOrder));
      const rule = getMarketRule(orderBias.orderType, marketMetricsRef.current);
      const aligned = !!orderBias.side && rule.aligned;

      setState({
        status: aligned ? "aligned" : "against",
        color: aligned ? "#22c55e" : "#ef4444",
        headline: aligned ? "PARENT ORDER IS WITH THE MARKET" : "WARNING: PARENT ORDER IS AGAINST THE MARKET",
        message: aligned
          ? `Latest parent ${orderBias.orderType} BUY order is aligned with volume, sellers, and PCR.`
          : `Latest parent ${orderBias.orderType || "UNKNOWN"} BUY order is not aligned. ${rule.expected}`,
        order: orderBias.latestBuy,
        liveCount: orderBias.liveCount,
        source,
      });
    } catch (e) {
      setState({ status: "error", color: "#f59e0b", headline: "PARENT ORDER CHECK FAILED", message: e.message || "Unable to fetch parent orders." });
    }
  }, [marketSide]);

  useEffect(() => {
    check();
    timerRef.current = setInterval(check, 5000);
    return () => clearInterval(timerRef.current);
  }, [check]);

  const color = state?.color || "#f59e0b";

  return (
    <div style={{ background: "#0a1628", border: `1px solid ${color}60`, borderRadius: 6, marginBottom: 16, overflow: "hidden" }}>
      <button onClick={() => setOpen(v => !v)} style={{ width: "100%", background: "#060d18", border: "none", color, padding: "10px 16px", textAlign: "left", fontSize: 11, letterSpacing: 3, fontWeight: "bold", cursor: "pointer" }}>
        {title} - EVERY 5 SECONDS - {state?.headline || "CHECKING..."} {open ? "▲" : "▼"}
      </button>

      {state?.status === "against" && (
        <div style={{ color: "#fff", background: "#7f1d1d", padding: "9px 16px", fontSize: 12, fontWeight: "bold" }}>
          {state.message}
        </div>
      )}

      {open && (
        <div style={{ padding: "14px 16px", color: "#cbd5e1", fontSize: 12 }}>
          <div style={{ color, fontSize: 18, fontWeight: "bold", marginBottom: 8 }}>{state?.headline || "CHECKING..."}</div>
          <div>{state?.message || "Fetching parent orders..."}</div>
          <div style={{ marginTop: 10, color: "#64748b", fontSize: 10, letterSpacing: 2 }}>
            MARKET: <b style={{ color }}>{marketSide || "WAIT"}</b> | BUY ORDERS CHECKED: <b>{state?.liveCount || 0}</b> | SOURCE: <b>{state?.source || "-"}</b>
            {state?.order && <> | RECENT BUY: <b>{state.order.symbol}</b></>}
          </div>
        </div>
      )}
    </div>
  );
}
